import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import type { TransactionItem } from '../TransaccionesPageNew';

// ----------------------------------------------------------------------
// Interfaces
// ----------------------------------------------------------------------
interface PayoutDetails {
    formOfPayment: string;
    accountNumber: string;
    gateway: string;
    externalReference: string;
}

interface Distribution {
    hostAmount: number;
    jomCommission: number;
    jomCommissionPercentaje: number;
}

interface Parties {
    hostName: string;
    guestName: string;
    propertyName: string;
}

interface PriceBreakdownItem {
    type: string;
    description: string;
    amount: number;
    percentage: number | null;
    date: string | null;
}

interface TransactionDetails {
    priceCalculationId: number;
    calculationStatusCode: string;
    transactionCode: string;
    createdAt: string;
    createdAt_Formatted: string;
    amount: number;
    commission: number;
    parties: Parties;
    priceBreakdown: PriceBreakdownItem[];
    payoutDetails: PayoutDetails | null;
    distribution: Distribution;
}

interface TransactionDetailProps {
    isOpen: boolean;
    transaction: TransactionItem | null;
    onClose: () => void;
}

export const TransaccionDetailDrawer: React.FC<TransactionDetailProps> = ({ isOpen, transaction, onClose }) => {

    const [details, setDetails] = useState<TransactionDetails | null>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchDetails = async () => {
            if (!isOpen || !transaction) {
                setDetails(null);
                setError(null);
                return;
            }

            setIsLoadingDetail(true);
            setError(null);

            try {
                const endpoint = `GetTransferTransactonReportDetails?transactionId=${transaction.priceCalculationId}`;
                const response = await apiClient.get<any>(endpoint);

                const rawResponse = response.data ? response.data : response;

                if (rawResponse.hasError) {
                    throw new Error(rawResponse.resultError?.message || "Error al obtener detalles");
                }

                const finalData = rawResponse.data ? rawResponse.data : rawResponse;

                setDetails(finalData);

            } catch (err: any) {
                console.error("Error al cargar detalles:", err);
                setError("No se pudieron cargar los detalles de la transacción.");
            } finally {
                setIsLoadingDetail(false);
            }
        };

        fetchDetails();
    }, [isOpen, transaction]);

    if (!transaction && isOpen) return null;

    const formatMoney = (val: number | undefined | null) => `Bs. ${(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <>
            <div
                className={`fixed inset-0 z-40 transition-opacity duration-300 bg-gray-900/30 backdrop-blur-[2px] ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                onClick={onClose}
            />

            <div
                className={`fixed inset-y-0 right-0 z-50 flex flex-col w-full sm:w-[350px] bg-white shadow-2xl transition-transform duration-300 ease-in-out transform border-l border-gray-200 ${isOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
            >
                <div className="flex items-center justify-between p-5 border-b shrink-0 border-gray-100 bg-white">
                    <h3 className="m-0 text-[11px] font-bold tracking-[1px] text-gray-400 uppercase">
                        Detalle de transacción
                    </h3>
                    <button onClick={onClose} className="flex items-center justify-center w-8 h-8 transition-colors rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-900">
                        ✕
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto hide-scrollbar relative bg-white">

                    {isLoadingDetail && (
                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
                            <Loader2 className="w-8 h-8 animate-spin text-[#4f6df5] mb-2" />
                            <span className="text-xs font-semibold text-gray-500">Cargando detalles...</span>
                        </div>
                    )}

                    {error && !isLoadingDetail && (
                        <div className="flex items-center justify-center h-full p-5 text-center">
                            <span className="text-sm font-semibold text-red-500">{error}</span>
                        </div>
                    )}

                    {/* === APLICAMOS ? A TODO PARA EVITAR CRASHES === */}
                    {details && !isLoadingDetail && (
                        <>
                            <div className="p-5 text-center border-b border-gray-100 bg-gray-50/50">
                                <div className="mb-2 font-mono text-[10px] text-[#4f6df5] opacity-70 break-all px-4">{details?.transactionCode}</div>
                                <div className="mb-1.5 font-mono text-[32px] font-bold text-emerald-500">
                                    {formatMoney(details?.amount)}
                                </div>
                                <div className="flex justify-center gap-2 mb-3">
                                    <span className="px-2.5 py-0.5 text-[10px] font-bold uppercase rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                                        {details?.calculationStatusCode}
                                    </span>
                                </div>
                                <div className="text-xs text-gray-500">{details?.createdAt_Formatted}</div>
                            </div>

                            <div className="p-5 border-b border-gray-100">
                                <div className="mb-4 text-[10px] font-bold tracking-[0.8px] text-gray-400 uppercase flex items-center gap-2">
                                    Partes involucradas
                                </div>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-gray-500">Huésped</span>
                                        <div className="flex items-center gap-2">
                                            {transaction?.guest?.avatarColor && (
                                                <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white shrink-0" style={{ background: transaction.guest.avatarColor }}>
                                                    {transaction.guest.initials}
                                                </div>
                                            )}
                                            <span className="text-xs font-bold text-gray-900">{details?.parties?.guestName || 'N/A'}</span>
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-start">
                                        <span className="text-xs text-gray-500">Anfitrión</span>
                                        <span className="text-xs font-bold text-gray-900">{details?.parties?.hostName || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between items-start">
                                        <span className="text-xs text-gray-500">Propiedad</span>
                                        <span className="text-xs font-bold text-gray-900 text-right ml-4">{details?.parties?.propertyName || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="p-5 border-b border-gray-100 bg-white">
                                <div className="mb-4 text-[10px] font-bold tracking-[0.8px] text-gray-400 uppercase flex items-center gap-2">
                                    Detalle del cálculo
                                </div>
                                <div className="space-y-2">
                                    {details?.priceBreakdown?.map((item, i) => (
                                        <div key={i} className="flex justify-between py-2 border-b border-dashed border-gray-100 last:border-0">
                                            <div className="flex flex-col">
                                                <span className="text-[11px] font-medium text-gray-700">{item?.description} {item?.percentage ? `(${(item.percentage * 100).toFixed(0)}%)` : ''}</span>
                                                {item?.date && (
                                                    <span className="text-[9px] text-gray-400 mt-0.5">
                                                        {new Date(item.date).toLocaleDateString('es-ES')}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="font-mono text-[11px] font-bold text-gray-900">{formatMoney(item?.amount)}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-between items-center py-2 mt-2 font-bold border-t border-gray-200">
                                    <span className="text-xs text-gray-900">Total calculado</span>
                                    <span className="font-mono text-[14px] text-emerald-600">{formatMoney(details?.amount)}</span>
                                </div>
                            </div>

                            <div className="p-5 border-b border-gray-100 bg-gray-50/20">
                                <div className="mb-4 text-[10px] font-bold tracking-[0.8px] text-gray-400 uppercase">
                                    Distribución Final
                                </div>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-xs text-gray-500">Neto para Anfitrión</span>
                                        <span className="font-mono text-xs font-bold text-emerald-600">{formatMoney(details?.distribution?.hostAmount)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-xs text-gray-500">Comisión JOM ({details?.distribution?.jomCommissionPercentaje ? (details.distribution.jomCommissionPercentaje * 100).toFixed(0) : 0}%)</span>
                                        <span className="font-mono text-xs font-bold text-amber-500">{formatMoney(details?.distribution?.jomCommission)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="p-5">
                                <div className="mb-4 text-[10px] font-bold tracking-[0.8px] text-gray-400 uppercase">
                                    Información de pago
                                </div>
                                {details?.payoutDetails ? (
                                    <div className="space-y-3">
                                        <div className="flex justify-between">
                                            <span className="text-xs text-gray-500">Método</span>
                                            <span className="text-xs font-bold text-gray-900">{details.payoutDetails.formOfPayment}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-xs text-gray-500">Cuenta</span>
                                            <span className="text-xs font-bold text-gray-900">{details.payoutDetails.accountNumber}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-xs text-gray-500">Gateway</span>
                                            <span className="text-xs font-bold text-gray-900">{details.payoutDetails.gateway}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs text-gray-500">Ref. Externa</span>
                                            <span className="text-[10px] font-mono bg-gray-100 p-2 rounded break-all">{details.payoutDetails.externalReference}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-[11px] italic text-gray-400 py-2">Información de pago pendiente de procesar.</div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="flex flex-col gap-2 p-5 shrink-0 border-t border-gray-100 mt-auto bg-white">
                    <button
                        disabled={isLoadingDetail || !details}
                        className="w-full py-2.5 text-xs font-semibold transition-colors bg-white border rounded-lg text-gray-700 border-gray-300 hover:bg-gray-50 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Descargar comprobante
                    </button>
                    <button
                        disabled={isLoadingDetail || !details}
                        className="w-full py-2.5 text-xs font-semibold transition-colors bg-white border rounded-lg text-red-600 border-red-200 hover:bg-red-50 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Iniciar reembolso
                    </button>
                </div>

            </div>
        </>
    );
};

export default TransaccionDetailDrawer;